import React, { createContext, useContext, useMemo, useReducer, useCallback } from 'react';
import { receiptService } from '@services/operator/receiptService';

const ReceiptPutawayContext = createContext(null);

// rowStates: { [receiptItemId]: Array<{ key, shelfId, location, quantity }> }
const newShelfRow = (key, overrides = {}) => ({
  key,                // client-side unique id for stable React keys
  shelfId: null,
  location: '',
  quantity: 0,
  ...overrides
});

// 產生 row key — 用 crypto.randomUUID（瀏覽器原生）保證唯一；
// 不在 reducer 內呼叫 Date.now() 等不純函式（破壞 reducer 純函數契約 + Strict Mode 雙呼會撞 key）。
const genRowKey = (itemId) => {
  const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `${itemId}-${uuid}`;
};

const initialState = (receipt) => {
  const items = receipt?.items || [];
  const rowStates = {};
  items.forEach(item => {
    const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
    if (remaining > 0) {
      rowStates[item.id] = [newShelfRow(genRowKey(item.id), { quantity: remaining })];
    }
  });
  return {
    receipt,
    items,
    rowStates,
    selection: new Set(),
    submitting: false,
    results: null  // { [receiptItemId]: { success, message?, interpolations? } }
  };
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_RECEIPT':
      return initialState(action.receipt);

    case 'UPDATE_SHELF_ROW': {
      const { itemId, rowKey, patch } = action;
      const rows = state.rowStates[itemId] || [];
      return {
        ...state,
        rowStates: {
          ...state.rowStates,
          [itemId]: rows.map(r => r.key === rowKey ? { ...r, ...patch } : r)
        }
      };
    }

    case 'ADD_SHELF_ROW': {
      const { itemId, defaultQuantity = 0 } = action;
      const rows = state.rowStates[itemId] || [];
      return {
        ...state,
        rowStates: {
          ...state.rowStates,
          [itemId]: [...rows, newShelfRow(action.rowKey, { quantity: defaultQuantity })]
        }
      };
    }

    case 'REMOVE_SHELF_ROW': {
      const { itemId, rowKey } = action;
      const rows = state.rowStates[itemId] || [];
      const next = rows.filter(r => r.key !== rowKey);
      return {
        ...state,
        rowStates: {
          ...state.rowStates,
          [itemId]: next.length ? next : [newShelfRow(action.fallbackRowKey, { quantity: 0 })]
        }
      };
    }

    case 'TOGGLE_SELECT': {
      const { itemId } = action;
      const next = new Set(state.selection);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return { ...state, selection: next };
    }

    case 'CLEAR_SELECTION':
      return { ...state, selection: new Set() };

    case 'BATCH_APPLY_SHELF': {
      // Overwrites shelf + quantity on all selected items.
      const { shelfId, location, rowKeyMap } = action;
      const nextRowStates = { ...state.rowStates };
      state.selection.forEach(itemId => {
        const item = state.items.find(i => i.id === itemId);
        if (!item) return;
        const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
        nextRowStates[itemId] = [newShelfRow(rowKeyMap[itemId], {
          shelfId,
          location,
          quantity: remaining
        })];
      });
      return { ...state, rowStates: nextRowStates };
    }

    case 'SET_SUBMITTING':
      return { ...state, submitting: action.value };

    case 'SET_RESULTS':
      // Merge so partial-retry preserves earlier successes (operator may submit
      // failed rows in a follow-up batch; we don't want previously-succeeded
      // rows to drop from the per-row UI feedback).
      return {
        ...state,
        submitting: false,
        results: { ...(state.results || {}), ...action.results }
      };

    case 'CLEAR_RESULTS':
      return { ...state, results: null };

    default:
      return state;
  }
}

export function ReceiptPutawayProvider({ initialReceipt, children }) {
  const [state, dispatch] = useReducer(reducer, initialReceipt, initialState);

  const actions = useMemo(() => ({
    setReceipt: (receipt) => dispatch({ type: 'SET_RECEIPT', receipt }),
    updateShelfRow: (itemId, rowKey, patch) => dispatch({ type: 'UPDATE_SHELF_ROW', itemId, rowKey, patch }),
    // rowKey 在 caller 側生成（dispatch 之前），保持 reducer 純函數
    addShelfRow: (itemId, defaultQuantity) => dispatch({ type: 'ADD_SHELF_ROW', itemId, defaultQuantity, rowKey: genRowKey(itemId) }),
    removeShelfRow: (itemId, rowKey) => dispatch({ type: 'REMOVE_SHELF_ROW', itemId, rowKey, fallbackRowKey: genRowKey(itemId) }),
    toggleSelect: (itemId) => dispatch({ type: 'TOGGLE_SELECT', itemId }),
    clearSelection: () => dispatch({ type: 'CLEAR_SELECTION' }),
    batchApplyShelf: ({ shelfId, location }) => {
      // 預先為每個被套用的 item 產生唯一 rowKey，dispatch 進去純資料即可
      const rowKeyMap = {};
      state.selection.forEach(itemId => { rowKeyMap[itemId] = genRowKey(itemId); });
      dispatch({ type: 'BATCH_APPLY_SHELF', shelfId, location, rowKeyMap });
    },
    clearResults: () => dispatch({ type: 'CLEAR_RESULTS' }),
  }), [state.selection]);

  const submit = useCallback(async () => {
    // Build request from rowStates, skipping items with no configured quantity
    const items = [];
    Object.entries(state.rowStates).forEach(([itemId, rows]) => {
      const shelves = rows
        .filter(r => r.shelfId && Number(r.quantity) > 0)
        .map(r => ({ shelf_id: r.shelfId, quantity: Number(r.quantity) }));
      if (shelves.length > 0) {
        items.push({ receipt_item_id: itemId, shelves });
      }
    });
    if (!items.length) return { results: [], success_count: 0, failure_count: 0 };

    dispatch({ type: 'SET_SUBMITTING', value: true });
    try {
      const res = await receiptService.batchPutaway(items);
      const payload = res?.data || { results: [], success_count: 0, failure_count: 0 };
      const byId = {};
      (payload.results || []).forEach(r => { byId[r.id] = r; });
      dispatch({ type: 'SET_RESULTS', results: byId });
      return payload;
    } catch (err) {
      // 網路/token/server error — 確保 submitting 被清除並將錯誤拋回讓 caller 顯示
      dispatch({ type: 'SET_SUBMITTING', value: false });
      throw err;
    }
  }, [state.rowStates]);

  const derived = useMemo(() => {
    const items = state.items;
    const totalReceivedQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
    const totalPutawayQty = items.reduce((s, i) => s + (i.putaway_quantity || 0), 0);

    let readyItemCount = 0;
    let readyQtyTotal = 0;
    items.forEach(item => {
      const remaining = (item.quantity || 0) - (item.putaway_quantity || 0);
      if (remaining <= 0) return;
      const rows = state.rowStates[item.id] || [];
      // Strict: require every row to have a resolved shelfId. A typed-but-
      // unresolved location is not a valid target (the backend will reject
      // it with shelf_not_found). Sum only counts resolved rows.
      const allResolved = rows.every(r => !!r.shelfId);
      const sumQty = rows.reduce((s, r) => s + (r.shelfId ? Number(r.quantity || 0) : 0), 0);
      if (allResolved && sumQty > 0 && sumQty <= remaining) {
        readyItemCount += 1;
        readyQtyTotal += sumQty;
      }
    });
    return { totalReceivedQty, totalPutawayQty, readyItemCount, readyQtyTotal };
  }, [state.items, state.rowStates]);

  const value = useMemo(() => ({ state, actions, submit, derived }),
    [state, actions, submit, derived]);

  return (
    <ReceiptPutawayContext.Provider value={value}>
      {children}
    </ReceiptPutawayContext.Provider>
  );
}

export function useReceiptPutaway() {
  const ctx = useContext(ReceiptPutawayContext);
  if (!ctx) throw new Error('useReceiptPutaway must be used inside ReceiptPutawayProvider');
  return ctx;
}
